import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, BellRing, CalendarClock, FlaskConical, Loader2, RotateCcw, Save } from "lucide-react";
import {
  DEFAULT_ALERTS,
  DEFAULT_DIGEST,
  WEBHOOK_URLS,
  WEEKDAYS,
  domainList,
  errorMessage,
  formatDate,
  hourLabel,
  parseDomains,
  type CommsSettings,
} from "@/lib/comms";
import { CopyButton, ErrorState } from "./shared";
import GuardPanel from "./GuardPanel";

const HOURS = Array.from({ length: 24 }, (_, i) => i);

function numberOr(value: string, fallback: number): number {
  const n = Number(value);
  return value.trim() === "" || Number.isNaN(n) ? fallback : n;
}

function ToggleRow({
  id,
  label,
  hint,
  checked,
  onChange,
  warn,
}: {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  warn?: boolean;
}) {
  return (
    <div className={`flex items-start justify-between gap-4 rounded-md border p-3 ${warn && checked ? "border-destructive" : ""}`}>
      <div className="min-w-0">
        <Label htmlFor={id} className="cursor-pointer">{label}</Label>
        <p className={`text-xs leading-snug ${warn && checked ? "text-destructive" : "text-muted-foreground"}`}>{hint}</p>
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onChange} data-testid={`switch-${id}`} />
    </div>
  );
}

export default function SettingsTab() {
  const { toast } = useToast();
  const [form, setForm] = useState<CommsSettings | null>(null);
  const [allowDomainsText, setAllowDomainsText] = useState("");
  const [restartOpen, setRestartOpen] = useState(false);

  const { data, isLoading, error } = useQuery<{ settings: CommsSettings }>({ queryKey: ["/api/admin/comms/settings"] });

  useEffect(() => {
    if (!data?.settings) return;
    setForm(data.settings);
    setAllowDomainsText((data.settings.sandbox?.allowDomains || []).join(", "));
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: async (body: Record<string, unknown>) => (await apiRequest("PUT", "/api/admin/comms/settings", body)).json(),
    onSuccess: (res: { settings?: CommsSettings }) => {
      if (res?.settings) {
        setForm(res.settings);
        setAllowDomainsText((res.settings.sandbox?.allowDomains || []).join(", "));
      }
      queryClient.invalidateQueries({ queryKey: ["/api/admin/comms/settings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/comms/overview"] });
      toast({ title: "Settings saved" });
    },
    onError: (e: unknown) => toast({ title: "Could not save the settings", description: errorMessage(e), variant: "destructive" }),
  });

  const restartMutation = useMutation({
    mutationFn: async () => (await apiRequest("PUT", "/api/admin/comms/settings", { warmup: { restart: true } })).json(),
    onSuccess: (res: { settings?: CommsSettings }) => {
      if (res?.settings) setForm(res.settings);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/comms/settings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/comms/overview"] });
      setRestartOpen(false);
      toast({ title: "Warm-up restarted", description: "Today's cap drops back to the starting volume." });
    },
    onError: (e: unknown) => toast({ title: "Could not restart the warm-up", description: errorMessage(e), variant: "destructive" }),
  });

  if (error) return <Card><CardContent><ErrorState error={error} /></CardContent></Card>;

  if (isLoading || !form) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-56" />)}
      </div>
    );
  }

  const set = <K extends keyof CommsSettings>(key: K, value: CommsSettings[K]) => setForm(f => (f ? { ...f, [key]: value } : f));

  const sandbox = form.sandbox || { enabled: false, allowDomains: [] };
  const alerts = form.alerts || DEFAULT_ALERTS;
  const digest = form.digest || DEFAULT_DIGEST;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    saveMutation.mutate({
      emailProvider: form.emailProvider,
      fromName: form.fromName,
      fromEmail: form.fromEmail,
      replyTo: form.replyTo,
      dailyEmailCap: form.dailyEmailCap,
      throttlePerMinute: form.throttlePerMinute,
      warmup: {
        enabled: form.warmup.enabled,
        startPerDay: form.warmup.startPerDay,
        doubleEveryDays: form.warmup.doubleEveryDays,
      },
      quietHours: { start: form.quietHours.start, end: form.quietHours.end },
      sandbox: { enabled: sandbox.enabled, allowDomains: parseDomains(allowDomainsText) },
      defaultCapDays: form.defaultCapDays,
      whatsappEnabled: form.whatsappEnabled,
      smsEnabled: form.smsEnabled,
      marketingToStudents: form.marketingToStudents,
      trackOpens: form.trackOpens,
      trackClicks: form.trackClicks,
      alerts: {
        bounceRatePct: alerts.bounceRatePct,
        complaintRatePct: alerts.complaintRatePct,
        minSample: alerts.minSample,
        queueStallMinutes: alerts.queueStallMinutes,
      },
      digest: { weekday: digest.weekday, hour: digest.hour },
    });
  };

  return (
    <div className="space-y-6">
      <GuardPanel />

      <form onSubmit={submit} className="space-y-6">
      {/* Sender */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Sending</CardTitle>
          <CardDescription>Who the mail comes from and which provider carries it.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="email-provider">Email provider</Label>
            <Select value={form.emailProvider} onValueChange={v => set("emailProvider", v as CommsSettings["emailProvider"])}>
              <SelectTrigger id="email-provider" className="sm:w-[280px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="resend">Resend</SelectItem>
                <SelectItem value="ses">Amazon SES</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Amazon SES needs the AWS environment variables set on the server and a verified sending domain before it will accept a single message.
              Resend is the default and needs only its API key.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="from-name">From name</Label>
            <Input id="from-name" value={form.fromName} onChange={e => set("fromName", e.target.value)} placeholder="EduFocal Academy" data-testid="input-from-name" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="from-email">From email</Label>
            <Input id="from-email" type="email" value={form.fromEmail} onChange={e => set("fromEmail", e.target.value)} placeholder="hello@edufocalacademy.com" data-testid="input-from-email" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="reply-to">Reply-to</Label>
            <Input id="reply-to" type="email" value={form.replyTo} onChange={e => set("replyTo", e.target.value)} placeholder="support@edufocalacademy.com" data-testid="input-reply-to" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="throttle">Throttle, emails per minute</Label>
            <Input
              id="throttle"
              type="number"
              min={1}
              value={form.throttlePerMinute}
              onChange={e => set("throttlePerMinute", numberOr(e.target.value, form.throttlePerMinute))}
              data-testid="input-throttle"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="daily-cap">Daily email cap</Label>
            <Input
              id="daily-cap"
              type="number"
              min={1}
              value={form.dailyEmailCap}
              onChange={e => set("dailyEmailCap", numberOr(e.target.value, form.dailyEmailCap))}
              data-testid="input-daily-cap"
            />
            <p className="text-xs text-muted-foreground">The ceiling. The warm-up schedule can only give a smaller number than this.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cap-days">Default frequency cap, days</Label>
            <Input
              id="cap-days"
              type="number"
              min={0}
              value={form.defaultCapDays}
              onChange={e => set("defaultCapDays", numberOr(e.target.value, form.defaultCapDays))}
              data-testid="input-cap-days"
            />
            <p className="text-xs text-muted-foreground">How many days a contact rests between messages from us.</p>
          </div>
        </CardContent>
      </Card>

      {/* Sandbox */}
      <Card className={sandbox.enabled ? "border-amber-400 dark:border-amber-700" : ""}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><FlaskConical className="w-4 h-4" /> Sandbox</CardTitle>
          <CardDescription>
            With sandbox on, nothing leaves the building except mail to the allow-listed domains. Every other send is still queued and recorded, but
            it lands with the status sandboxed instead of going out, and the phone channels, WhatsApp and SMS, are blocked entirely. This is on by
            default on the dev server. Turn it off on production only.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ToggleRow
            id="sandbox-enabled"
            label="Sandbox mode"
            hint={sandbox.enabled
              ? `On. Only ${domainList(sandbox.allowDomains)} will actually receive anything.`
              : "Off. Every send goes to the real address on the contact. This is the production setting."}
            checked={sandbox.enabled}
            onChange={v => set("sandbox", { ...sandbox, enabled: v })}
          />
          <div className="space-y-1.5">
            <Label htmlFor="sandbox-domains">Allowed domains</Label>
            <Input
              id="sandbox-domains"
              value={allowDomainsText}
              onChange={e => setAllowDomainsText(e.target.value)}
              placeholder="edufocal.com, edufocalacademy.com"
              className="font-mono text-xs"
              data-testid="input-sandbox-domains"
            />
            <p className="text-xs text-muted-foreground">
              Comma separated. An address is delivered only when the part after the @ matches one of these exactly. Leave the list empty and nothing
              is delivered at all while sandbox is on.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Warm-up */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="text-base">Warm-up</CardTitle>
              <CardDescription>
                A new sending domain that suddenly blasts thousands of emails gets filtered. The warm-up raises the daily allowance gradually
                instead.
              </CardDescription>
            </div>
            <Button type="button" variant="outline" onClick={() => setRestartOpen(true)} data-testid="button-restart-warmup">
              <RotateCcw className="w-4 h-4 mr-2" /> Restart warm-up
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <ToggleRow
            id="warmup-enabled"
            label="Warm-up enabled"
            hint="Off means the full daily cap is available immediately."
            checked={form.warmup.enabled}
            onChange={v => set("warmup", { ...form.warmup, enabled: v })}
          />
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="warmup-start">Start per day</Label>
              <Input
                id="warmup-start"
                type="number"
                min={1}
                value={form.warmup.startPerDay}
                onChange={e => set("warmup", { ...form.warmup, startPerDay: numberOr(e.target.value, form.warmup.startPerDay) })}
                data-testid="input-warmup-start"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="warmup-double">Double every, days</Label>
              <Input
                id="warmup-double"
                type="number"
                min={1}
                value={form.warmup.doubleEveryDays}
                onChange={e => set("warmup", { ...form.warmup, doubleEveryDays: numberOr(e.target.value, form.warmup.doubleEveryDays) })}
                data-testid="input-warmup-double"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Started</Label>
              <div className="h-10 flex items-center text-sm text-muted-foreground" data-testid="text-warmup-started">
                {formatDate(form.warmup.startedAt)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quiet hours */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Quiet hours</CardTitle>
          <CardDescription>
            Nothing goes out between these hours, Jamaica time. Anything that comes due inside the window waits until it closes.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="quiet-start">Quiet hours start</Label>
            <Select value={String(form.quietHours.start)} onValueChange={v => set("quietHours", { ...form.quietHours, start: Number(v) })}>
              <SelectTrigger id="quiet-start"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {HOURS.map(h => <SelectItem key={h} value={String(h)}>{hourLabel(h)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="quiet-end">Quiet hours end</Label>
            <Select value={String(form.quietHours.end)} onValueChange={v => set("quietHours", { ...form.quietHours, end: Number(v) })}>
              <SelectTrigger id="quiet-end"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {HOURS.map(h => <SelectItem key={h} value={String(h)}>{hourLabel(h)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Channels and tracking */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Channels and tracking</CardTitle>
          <CardDescription>What may be sent, and what we record about it.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2">
          <ToggleRow
            id="whatsapp-enabled"
            label="WhatsApp enabled"
            hint="Off refuses any WhatsApp campaign or automation at launch."
            checked={form.whatsappEnabled}
            onChange={v => set("whatsappEnabled", v)}
          />
          <ToggleRow
            id="sms-enabled"
            label="SMS enabled"
            hint="Off refuses any SMS campaign or automation at launch."
            checked={form.smsEnabled}
            onChange={v => set("smsEnabled", v)}
          />
          <ToggleRow
            id="track-opens"
            label="Track opens"
            hint="Adds an invisible pixel to each email. Turning it off makes the open rate meaningless."
            checked={form.trackOpens}
            onChange={v => set("trackOpens", v)}
          />
          <ToggleRow
            id="track-clicks"
            label="Track clicks"
            hint="Rewrites links so clicks are counted. Turning it off makes the click rate meaningless."
            checked={form.trackClicks}
            onChange={v => set("trackClicks", v)}
          />
          <div className="sm:col-span-2">
            <ToggleRow
              id="marketing-to-students"
              label="Allow marketing to students"
              hint={form.marketingToStudents
                ? "On. Promotional mail can now reach children's own contact details. This is off for a reason: leave it on only for as long as you need it."
                : "Off. Marketing only ever reaches parent contacts. Leave it off."}
              checked={form.marketingToStudents}
              onChange={v => set("marketingToStudents", v)}
              warn
            />
          </div>
          {form.marketingToStudents && (
            <p className="sm:col-span-2 text-sm text-destructive flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              Marketing to students is enabled. Every marketing campaign will now include student contacts.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Alerts and the circuit breaker */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><BellRing className="w-4 h-4" /> Alerts and circuit breaker</CardTitle>
          <CardDescription>
            The thresholds the breaker watches. Cross one on a large enough sample and sending stops by itself, with a Telegram alert. Bounce and
            complaint rates are measured over the last 24 hours. Keep the complaint rate well under a tenth of a percent: the mailbox providers
            start filtering long before they tell you.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="alert-bounce">Bounce rate, percent</Label>
            <Input
              id="alert-bounce"
              type="number"
              min={0}
              max={100}
              step="0.1"
              value={alerts.bounceRatePct}
              onChange={e => set("alerts", { ...alerts, bounceRatePct: numberOr(e.target.value, alerts.bounceRatePct) })}
              data-testid="input-alert-bounce"
            />
            <p className="text-xs text-muted-foreground">Above this and the queue is held. 5 is a sensible ceiling; 2 or lower is healthy.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="alert-complaint">Complaint rate, percent</Label>
            <Input
              id="alert-complaint"
              type="number"
              min={0}
              max={100}
              step="0.01"
              value={alerts.complaintRatePct}
              onChange={e => set("alerts", { ...alerts, complaintRatePct: numberOr(e.target.value, alerts.complaintRatePct) })}
              data-testid="input-alert-complaint"
            />
            <p className="text-xs text-muted-foreground">Marked as spam. Gmail's own limit is 0.3%, and it does not warn you first.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="alert-sample">Minimum sample</Label>
            <Input
              id="alert-sample"
              type="number"
              min={1}
              value={alerts.minSample}
              onChange={e => set("alerts", { ...alerts, minSample: numberOr(e.target.value, alerts.minSample) })}
              data-testid="input-alert-sample"
            />
            <p className="text-xs text-muted-foreground">
              How many messages must have gone out in the window before a rate counts. Without this, one bounce out of three reads as 33%.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="alert-stall">Queue stall, minutes</Label>
            <Input
              id="alert-stall"
              type="number"
              min={1}
              value={alerts.queueStallMinutes}
              onChange={e => set("alerts", { ...alerts, queueStallMinutes: numberOr(e.target.value, alerts.queueStallMinutes) })}
              data-testid="input-alert-stall"
            />
            <p className="text-xs text-muted-foreground">
              Alert when the oldest message that is due has been waiting longer than this. It usually means the worker has stopped, not that the
              queue is busy.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Weekly digest */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><CalendarClock className="w-4 h-4" /> Weekly digest</CardTitle>
          <CardDescription>
            When the weekly progress digest goes out to parents, Jamaica time. The digest automation fires in this slot, and it still respects the
            progress topic, quiet hours and the daily cap.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="digest-weekday">Day</Label>
            <Select value={String(digest.weekday)} onValueChange={v => set("digest", { ...digest, weekday: Number(v) })}>
              <SelectTrigger id="digest-weekday" data-testid="select-digest-weekday"><SelectValue /></SelectTrigger>
              <SelectContent>
                {WEEKDAYS.map(w => <SelectItem key={w.id} value={String(w.id)}>{w.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="digest-hour">Hour</Label>
            <Select value={String(digest.hour)} onValueChange={v => set("digest", { ...digest, hour: Number(v) })}>
              <SelectTrigger id="digest-hour" data-testid="select-digest-hour"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {HOURS.map(h => <SelectItem key={h} value={String(h)}>{hourLabel(h)}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Jamaica time. Pick an hour outside quiet hours or the digest waits for the window to close.</p>
          </div>
        </CardContent>
      </Card>

      {/* Webhooks */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Webhook URLs</CardTitle>
          <CardDescription>
            Configure these at the providers. Without them, bounces, complaints and delivery receipts never reach us and the health numbers on the
            Overview tab stay empty.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {WEBHOOK_URLS.map(w => (
            <div key={w.url} className="space-y-1.5">
              <Label htmlFor={`webhook-${w.label}`}>{w.label}</Label>
              <div className="flex gap-2">
                <Input id={`webhook-${w.label}`} value={w.url} readOnly className="font-mono text-xs" onFocus={e => e.currentTarget.select()} />
                <CopyButton text={w.url} label={`${w.label} webhook URL`} />
              </div>
              <p className="text-xs text-muted-foreground">{w.hint}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button type="submit" disabled={saveMutation.isPending} data-testid="button-save-settings">
          {saveMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-2" /> Save settings</>}
        </Button>
      </div>

      <AlertDialog open={restartOpen} onOpenChange={setRestartOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restart the warm-up clock?</AlertDialogTitle>
            <AlertDialogDescription>
              Today's cap drops back to {form.warmup.startPerDay} emails and climbs again from there. Do this after a deliverability problem, or when
              the sending domain changes. Any queued campaign will take longer to finish.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => restartMutation.mutate()} data-testid="button-confirm-restart-warmup">
              {restartMutation.isPending ? "Restarting…" : "Restart warm-up"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </form>
    </div>
  );
}
